import { Component, inject, OnInit, signal, computed, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CurrencyPipe, DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DrawerModule } from 'primeng/drawer';
import { FundedDealsPortalEndpoint } from '@api/services/funded-deals-portal-endpoint';
import { FundedDeal, FundedDealsFilterOptions } from '@api/models/funded-deals-portal.model';
import { ImageComponent } from 'src/app/shared/components/image/image.component';

interface LoanAmountOption {
  label: string;
  min: number;
  max: number | null;
}

interface StateOption {
  label: string;
  value: string;
}

const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

@Component({
  selector: 'app-funded-deals',
  imports: [SelectModule, MultiSelectModule, DrawerModule, FormsModule, CurrencyPipe, DecimalPipe, NgClass, ImageComponent],
  templateUrl: './funded-deals.component.html'
})
export class FundedDealsComponent implements OnInit {
  private fundedDealsService = inject(FundedDealsPortalEndpoint);

  @ViewChild('filterBar', { read: ElementRef }) filterBarRef!: ElementRef<HTMLElement>;

  isScrolled = signal(false);

  @HostListener('window:scroll')
  onScroll() {
    const scrolled = (window.pageYOffset || document.documentElement.scrollTop) > 100
      && this.deals().length > 0;
    this.isScrolled.set(scrolled);
  }

  readonly trackRecord = [
    { icon: 'account_balance_wallet', label: 'Total Capital Deployed', value: '$5 Bn+' },
    { icon: 'home_work', label: 'Assets Under Management', value: '$1 Bn+' },
    { icon: 'account_balance', label: 'Years in Business', value: '26+' },
    { icon: 'distance', label: 'States We Lend in', value: '50' },
  ];

  readonly loanAmountOptions: LoanAmountOption[] = [
    { label: '$1M - $3M', min: 1_000_000, max: 3_000_000 },
    { label: '$3M - $5M', min: 3_000_000, max: 5_000_000 },
    { label: '$5M - $10M', min: 5_000_000, max: 10_000_000 },
    { label: '$10M+', min: 10_000_000, max: null },
  ];

  filterOptions = signal<FundedDealsFilterOptions>({ propertyTypes: [], loanPurposes: [], states: [] });

  stateOptions = computed<StateOption[]>(() =>
    (this.filterOptions().states ?? []).map(code => ({
      value: code,
      label: US_STATES[code] ?? code,
    }))
  );
  deals = signal<FundedDeal[]>([]);
  totalElements = signal(0);
  loading = signal(false);

  selectedPropertyTypes: string[] = [];
  selectedLoanPurposes: string[] = [];
  selectedStates: string[] = [];
  selectedLoanAmount: LoanAmountOption | null = null;

  mobileFiltersVisible = false;

  get activeFilterCount(): number {
    return (this.selectedPropertyTypes.length > 0 ? 1 : 0) +
      (this.selectedLoanPurposes.length > 0 ? 1 : 0) +
      (this.selectedStates.length > 0 ? 1 : 0) +
      (this.selectedLoanAmount ? 1 : 0);
  }

  private currentPage = 0;
  readonly pageSize = 24;

  ngOnInit(): void {
    this.fundedDealsService.getFilterOptions().subscribe(options => {
      this.filterOptions.set(options);
    });
    this.loadDeals(true);
  }

  onFilterChange(): void {
    this.loadDeals(true);
  }

  clearFilters(): void {
    this.selectedPropertyTypes = [];
    this.selectedLoanPurposes = [];
    this.selectedStates = [];
    this.selectedLoanAmount = null;
    this.mobileFiltersVisible = false;
    this.loadDeals(true);
  }

  loadMore(): void {
    this.currentPage++;
    this.loadDeals(false);
  }

  private loadDeals(reset: boolean): void {
    if (reset) {
      this.currentPage = 0;
      this.deals.set([]);
    }
    this.loading.set(true);
    this.fundedDealsService.getFundedDeals({
      propertyTypes: this.selectedPropertyTypes.length ? this.selectedPropertyTypes : undefined,
      loanPurposes: this.selectedLoanPurposes.length ? this.selectedLoanPurposes : undefined,
      states: this.selectedStates.length ? this.selectedStates : undefined,
      minLoanAmount: this.selectedLoanAmount?.min ?? undefined,
      maxLoanAmount: this.selectedLoanAmount?.max ?? undefined,
    }, {
      page: this.currentPage,
      size: this.pageSize,
    }).subscribe(page => {
      const incoming = page.content ?? [];
      this.deals.update(current => reset ? incoming : [...current, ...incoming]);
      this.totalElements.set(page.totalElements ?? 0);
      this.loading.set(false);
    });
  }
}
